import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import { apiGetJson as apiGet, apiPostJson as apiPost, apiPatchJson as apiPatch } from '../../../utils/api';

export default function PortfolioCompanyDetail({ companyId, year, onYearChange, onOpenClient }) {
  const qc = useQueryClient();
  const [sort, setSort] = useState({ key: 'name', dir: 1 });
  const [selected, setSelected] = useState(() => new Set());

  // Reset selection when the company or year changes.
  useEffect(() => { setSelected(new Set()); }, [companyId, year]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portfolio-company', companyId, year],
    queryFn: () => apiGet(`/admin/audit_tracker/portfolio/companies/${companyId}?year=${year}`),
    enabled: !!companyId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['portfolio-company', companyId, year] });
    qc.invalidateQueries({ queryKey: ['portfolio-companies'] });
  };

  const provision = useMutation({
    mutationFn: (propertyIds) => apiPost(`/admin/audit_tracker/portfolio/companies/${companyId}/provision`, {
      audit_year: year, include_children: true, property_ids: propertyIds && propertyIds.length ? propertyIds : undefined,
    }),
    onSuccess: (res) => {
      toast.success(`Provisioned: ${res.created} new, ${res.updated} updated, ${res.skipped.length} skipped`);
      setSelected(new Set());
      invalidate();
      if (res.client_id) onOpenClient?.(res.client_id, year); // drop into the company's workspace (same year)
    },
    onError: () => toast.error('Provisioning failed'),
  });

  const resync = useMutation({
    mutationFn: () => apiPost(`/admin/audit_tracker/portfolio/companies/${companyId}/resync`, { audit_year: year }),
    onSuccess: (res) => { toast.success(`Re-synced: ${res.added} added, ${res.updated} updated, ${res.stale} stale`); invalidate(); },
    onError: () => toast.error('Re-sync failed (no active audit for this year?)'),
  });

  const archive = useMutation({
    mutationFn: ({ id, restore }) => apiPatch(`/admin/audit_tracker/properties/${id}/${restore ? 'restore' : 'archive'}`, {}),
    onSuccess: invalidate,
    onError: () => toast.error('Could not update property'),
  });

  const properties = useMemo(() => {
    const rows = data?.properties ? [...data.properties] : [];
    rows.sort((a, b) => {
      const av = a[sort.key] ?? '';
      const bv = b[sort.key] ?? '';
      return (av > bv ? 1 : av < bv ? -1 : 0) * sort.dir;
    });
    return rows;
  }, [data, sort]);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now + 1, now, now - 1, now - 2];
  }, []);

  const hasAudit = properties.some((p) => p.audit_property_id);

  if (!companyId) return <div className="text-gray-500 dark:text-gray-400 p-6 text-center">Select a company to see its portfolio.</div>;
  if (isLoading) return <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 m-6" />;
  if (isError) return <div className="text-red-600 p-6">Could not load this company.</div>;

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allIds = properties.map((p) => p.processor_property_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));

  const th = (key, label) => (
    <th className="p-2 cursor-pointer select-none" onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
      {label}{sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const busy = provision.isPending || resync.isPending;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <h3 className="font-dm-serif text-lg dark:text-white">{data.company.name}</h3>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {data.company.state || '-'} · {data.company.child_count} children
            {data.company.contact_email && <> · {data.company.contact_email}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.company.client_id && onOpenClient && (
            <button onClick={() => onOpenClient(data.company.client_id, year)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              Open workspace
            </button>
          )}
          <select
            value={year}
            onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Action toolbar */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => provision.mutate([...selected])}
          disabled={busy || selected.size === 0}
          className="px-3 py-1.5 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 disabled:opacity-40"
        >
          {hasAudit ? 'Add selected' : 'Provision selected'} ({selected.size})
        </button>
        <button
          onClick={() => provision.mutate([])}
          disabled={busy}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40"
        >
          {hasAudit ? 'Add all' : 'Provision all'}
        </button>
        {hasAudit && (
          <button
            onClick={() => resync.mutate()}
            disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
          >
            <RefreshCw size={14} /> Re-sync from processor
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-200 sticky top-0">
            <tr>
              <th className="p-2"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              {th('name', 'Property')}{th('state', 'State')}{th('units', 'Units')}
              <th className="p-2">Contact</th>{th('to_review', 'Files (to/recv/done)')}{th('pct_complete', '%')}
              <th className="p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((p) => (
              <tr key={p.processor_property_id} className={`border-b border-gray-200 dark:border-gray-700 ${p.excluded ? 'opacity-50' : ''}`}>
                <td className="p-2"><input type="checkbox" checked={selected.has(p.processor_property_id)} onChange={() => toggle(p.processor_property_id)} /></td>
                <td className="p-2 dark:text-gray-200">{p.name}{p.company_name !== data.company.name && <span className="text-xs text-gray-400"> · {p.company_name}</span>}</td>
                <td className="p-2 dark:text-gray-300">{p.state || '-'}</td>
                <td className="p-2 dark:text-gray-300">{p.units ?? '-'}</td>
                <td className="p-2 dark:text-gray-300">{p.contact_email || '-'}</td>
                <td className="p-2 dark:text-gray-300">{p.to_review}/{p.received}/{p.completed}</td>
                <td className="p-2 dark:text-gray-300">{p.pct_complete}%</td>
                <td className="p-2 whitespace-nowrap">
                  {p.provisioned && <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">provisioned</span>}
                  {p.stale && <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">stale</span>}
                  {p.excluded && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">archived</span>}
                  {p.audit_property_id && (
                    <button
                      onClick={() => archive.mutate({ id: p.audit_property_id, restore: p.excluded })}
                      className="ml-2 text-xs text-blue-600 hover:underline"
                    >
                      {p.excluded ? 'Restore' : 'Remove'}
                    </button>
                  )}
                  {!p.audit_property_id && <span className="text-xs text-gray-400">-</span>}
                </td>
              </tr>
            ))}
            {properties.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-gray-500 dark:text-gray-400">No active properties.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
